import TablePagination from '../component/TablePagination';
import React, { useEffect, useState } from 'react';
import { brand } from '../config/brands';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend } from 'chart.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { PencilIcon, TrashIcon, EyeIcon, ShoppingBagIcon, BanknotesIcon, ChartBarIcon, CalculatorIcon } from '@heroicons/react/24/outline';
import { newestOrderFirst } from '../utils/orderSort';
import './Report.css';
import './OrderModals.css';

ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);
const amount = order => Number(order.totalAmount) || 0;
// Preserve the existing report's store delivery-charge conversion.
const subtotal = order => amount(order) - (order.businessName ? (Number(order.deliveryCharge) || 0) / 89500 : 0);
const money = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
const formatDate = value => value ? new Date(value).toLocaleDateString('en-GB') : '—';
const getStatusClass = status => ({ Pending: 'bg-customColor text-yellow', Cancelled: 'bg-lightRed text-red', Confirmed: 'bg-lightBlue text-blue' }[status] || 'bg-lightGreen text-green');

const Report = ({ orders = [], userRole, loading = false, error = '' }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const openModal = order => { setSelectedOrder(order); setIsModalOpen(true); };
  const closeModal = () => { setSelectedOrder(null); setIsModalOpen(false); };
  const handleEditItem = item => console.log('Edit item:', item);
  const handleDeleteItem = index => setSelectedOrder(order => ({ ...order, items: order.items.filter((_, i) => i !== index) }));

  const years = [...new Set([new Date().getFullYear(), ...orders.map(order => new Date(order.date).getFullYear()).filter(Number.isFinite)])].sort((a,b) => b-a);
  const businesses = [...new Set(orders.map(order => order.businessName || 'Butler'))].sort();
  const filteredOrders = orders.filter(order => {
    const date = order.date ? new Date(order.date) : null;
    if (!date || Number.isNaN(date.getTime()) || date.getFullYear() !== Number(selectedYear)) return false;
    if (selectedBusiness && (order.businessName || 'Butler') !== selectedBusiness) return false;
    if (fromDate && date < fromDate) return false;
    if (toDate) { const end = new Date(toDate); end.setHours(23,59,59,999); if (date > end) return false; }
    return [order.orderNumber, order.customerName, order.customerPhoneNumber, order.driverName, order.driverPhoneNumber, order.businessName, order.orderStatus?.name, formatDate(order.date)].some(value => String(value || '').toLowerCase().includes(searchTerm.toLowerCase()));
  }).sort(newestOrderFirst);
  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);
  useEffect(() => { setCurrentPage(1); }, [selectedYear, selectedBusiness, fromDate, toDate, searchTerm]);
  useEffect(() => { setCurrentPage(page => Math.min(page, Math.max(1, totalPages))); }, [totalPages]);
  const currentOrders = filteredOrders.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const handlePageChange = page => { if (page >= 1 && page <= totalPages) setCurrentPage(page); };
  const clearFilter = () => { setSelectedBusiness(''); setFromDate(null); setToDate(null); setSearchTerm(''); setSelectedYear(String(new Date().getFullYear())); };
  const total = filteredOrders.reduce((sum, order) => sum + amount(order), 0);
  const totalSubtotal = filteredOrders.reduce((sum, order) => sum + subtotal(order), 0);
  const monthly = Array(12).fill(0);
  filteredOrders.forEach(order => { monthly[new Date(order.date).getMonth()] += amount(order); });
  const chartData = {
    labels: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
    datasets: [{ label: 'Order value (USD)', data: monthly, backgroundColor: brand.theme.secondary, hoverBackgroundColor: brand.theme.primary, borderRadius: 6, maxBarThickness: 36 }],
  };
  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { backgroundColor: '#26344b', padding: 12, displayColors: false, callbacks: { label: context => money(context.parsed.y) } } },
    scales: { x: { grid: { display: false }, border: { display: false }, ticks: { color: '#929cad', font: { size: 11 } } }, y: { beginAtZero: true, border: { display: false }, grid: { color: '#f0f3f7' }, ticks: { color: '#929cad', maxTicksLimit: 5, callback: value => '$' + Number(value).toLocaleString() } } },
  };
  const metrics = [
    { label: 'Total order value', value: money(total), note: 'Including delivery charges', Icon: BanknotesIcon },
    { label: 'Order subtotal', value: money(totalSubtotal), note: 'Store delivery charges excluded', Icon: CalculatorIcon },
    { label: 'Orders', value: filteredOrders.length.toLocaleString(), note: 'Across all order statuses', Icon: ShoppingBagIcon },
    { label: 'Average order value', value: money(filteredOrders.length ? total / filteredOrders.length : 0), note: 'Total value divided by orders', Icon: ChartBarIcon },
  ];

  return (
    <section id="performance" className="report-page" aria-label="Performance and order breakdown">
        <header className="report-header"><div><p className="report-eyebrow">PERFORMANCE</p><h2>Performance &amp; reports</h2><p>Explore your history. These filters only affect the report below.</p></div></header>
        {error && <div className="report-error" role="alert">{error}</div>}
        <section className="report-filters" aria-label="Report filters">
          <label>Year<select value={selectedYear} onChange={event => { setSelectedYear(event.target.value); setFromDate(null); setToDate(null); }}>{years.map(year => <option key={year}>{year}</option>)}</select></label>
          {userRole === 'ROLE_ADMIN' && <label>Business<select value={selectedBusiness} onChange={event => setSelectedBusiness(event.target.value)}><option value="">All businesses</option>{businesses.map(name => <option key={name}>{name}</option>)}</select></label>}
          <div><label htmlFor="report-from">From date</label><DatePicker id="report-from" selected={fromDate} onChange={setFromDate} minDate={new Date(Number(selectedYear),0,1)} maxDate={toDate || new Date(Number(selectedYear),11,31)} dateFormat="dd/MM/yyyy" placeholderText="Start of year" isClearable /></div>
          <div><label htmlFor="report-to">To date</label><DatePicker id="report-to" selected={toDate} onChange={setToDate} minDate={fromDate || new Date(Number(selectedYear),0,1)} maxDate={new Date(Number(selectedYear),11,31)} dateFormat="dd/MM/yyyy" placeholderText="End of year" isClearable /></div>
          <button onClick={clearFilter}>Reset filters</button>
        </section>
        <section className="report-metrics" aria-label="Report summary">{metrics.map(({label,value,note,Icon}) => <div className="report-metric" key={label}><div><p>{label}</p><strong>{loading ? '…' : error ? '—' : value}</strong><small>{note}</small></div><span><Icon /></span></div>)}</section>
        <section className="report-chart-card"><div className="report-chart-heading"><div><h2>Monthly order value</h2><p>{selectedYear} · {selectedBusiness || 'All businesses'} · Current filters · All statuses</p></div><span className="report-chart-key"><i />Order value (USD)</span></div>
          <div className="report-chart">{loading ? <div className="report-empty" role="status">Loading chart…</div> : error ? <div className="report-empty">Chart data is unavailable.</div> : <Bar data={chartData} options={chartOptions} aria-label={`Monthly order value for ${selectedYear}`} role="img" />}</div>
          {!loading && !error && !filteredOrders.length && <p className="report-chart-note">No orders match the current filters.</p>}
        </section>
        <div className="data-card data-report">
          <div className="data-toolbar report-table-heading"><div><h2>Order breakdown</h2><p>The orders behind your report · Amounts in USD</p></div><input type="search" aria-label="Search report orders" placeholder="Search orders, customers, drivers…" value={searchTerm} onChange={event => setSearchTerm(event.target.value)} /></div>
        <div className="data-scroll overflow-x-auto"><table className="data-table min-w-full bg-white border-b border-r border-l border-gray-200 shadow-sm">
          <thead>
            <tr className="bg-grey border-b">
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700"></th>
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700">#</th>
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700">Status</th>
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700">Date</th>
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700">Time</th>
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700">SubTotal</th>
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700">Total</th>
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700">Store</th>
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700">Customer Name</th>
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700">Customer No.</th>
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700">Driver Name</th>
              <th className="px-4 py-3 font-inter text-left text-sm font-semibold text-gray-700">Driver No.</th>
            </tr>
          </thead>
          <tbody>
            {!loading && currentOrders.map((order, index) => (
              <tr key={order.orderId} className="border-b hover:bg-gray-50">
                <td
                  className="px-4 py-3 text-sm text-yellow font-inter cursor-pointer"
                  onClick={() => openModal(order)}
                >
                  <EyeIcon className="h-5 w-5 mr-2" />
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 font-inter font-medium">{order.orderNumber}</td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-1 font-inter font-medium rounded-full text-sm ${getStatusClass(
                      order.orderStatus?.name
                    )}`}
                  >
                    {order.orderStatus?.name}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 font-inter font-medium">
                  {formatDate(order.date)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 font-inter font-medium">
                  {order.timeCreated}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 font-inter font-medium">
                  {money(subtotal(order))}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 font-inter font-medium">
                  {money(amount(order))}
                </td>
                
                <td className="px-4 py-3">{order.businessName ? order.businessName:"Butler"}</td>
              
           
                <td className="px-4 py-3 text-sm text-gray-700 font-inter font-medium">
                  {order.customerName}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 font-inter font-medium">
                  {order.customerPhoneNumber}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 font-inter font-medium">
                  {order.driverName}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 font-inter font-medium">
                  {order.driverPhoneNumber}
                </td>
              </tr>
            ))}
          {!loading && !currentOrders.length && <tr><td colSpan="12"><div className="report-empty">{error ? 'Report data is unavailable.' : 'No orders match these filters.'}</div></td></tr>}
            {loading && <tr><td colSpan="12"><div className="report-empty" role="status">Loading report…</div></td></tr>}
          </tbody>
        </table></div>
        <div className="data-pagination bg-grey mb-0 w-full flex justify-between border-t border-l pl-3 border-r pr-3 pt-3 items-center md:flex-row justify-between pt-4">
          <span className="text-sm font-medium">
            Showing {filteredOrders.length ? (currentPage - 1) * rowsPerPage + 1 : 0}-{Math.min(currentPage * rowsPerPage, filteredOrders.length)} of{' '}
            {filteredOrders.length}
          </span>
          <TablePagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>

           {/* Modal */}
           {isModalOpen && selectedOrder && (
           <div className="order-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
           <div className="order-modal-panel report-order-modal bg-white max-w-full sm:max-w-2xl md:max-w-3xl w-full rounded-lg shadow-lg p-6 overflow-y-auto max-h-[90vh]">
              <h2 className="text-lg font-semibold mb-4">Order Details</h2>
              <hr className="w-full" style={{ border: "1px solid var(--color-border)", margin: "10px 0" }} />

              {/* Other Order Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
               
                <input
                  type="text"
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
                  defaultValue={selectedOrder.orderStatus && selectedOrder.orderStatus.name}
                />

<label className="block text-sm font-medium text-gray-700 mb-1">SubTotal</label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
          defaultValue={
            selectedOrder
              ?(Number(selectedOrder.totalAmount) - (Number(selectedOrder.deliveryCharge) / 89500)).toFixed(2)
              : 0
          }
        />

        
        <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Charge</label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
          defaultValue={selectedOrder.deliveryCharge}
        />

                <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
                <input
                  type="text"
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
                  defaultValue={selectedOrder.totalAmount}
                />


              </div>
               {/* Address Details */}
      {!selectedOrder.pickup ? <div className="mb-4">
        <h4 className="text-md font-semibold mb-2">Address Details</h4>
        {selectedOrder?.address?.street && <p className="text-sm text-gray-700">
          <strong>Street:</strong> {selectedOrder?.address?.street || "N/A"}
        </p>}
        {selectedOrder?.address?.building && <p className="text-sm text-gray-700">
          <strong>Building:</strong> {selectedOrder?.address?.building || "N/A"}
        </p>}
        {selectedOrder?.address?.apartment && <p className="text-sm text-gray-700">
          <strong>Apartment:</strong> {selectedOrder?.address?.apartment || "N/A"}
        </p>}
        {selectedOrder?.address?.instructions && <p className="text-sm text-gray-700">
          <strong>Instructions:</strong> {selectedOrder?.address?.instructions || "N/A"}
        </p>}
        {selectedOrder?.address?.google_map_coordinates && <p className="text-sm text-gray-700">
          <strong>Location:</strong> 
          <a 
            href={`https://www.google.com/maps?q=${selectedOrder?.address?.google_map_coordinates.x},${selectedOrder?.address?.google_map_coordinates.z}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-500 underline"
          >
            View on Google Maps
          </a>
        </p>}
      </div>:<div className="mb-4"> <h4 className="text-md font-semibold mb-2">Address Details</h4><p>Store Pickup</p></div>}

      {/* Address to Details */}
      {selectedOrder.addressTo && <div className="mb-4">
        <h4 className="text-md font-semibold mb-2">To Address Details</h4>
        {selectedOrder?.addressTo?.street && <p className="text-sm text-gray-700">
          <strong>Street:</strong> {selectedOrder?.addressTo?.street || "N/A"}
        </p>}
        {selectedOrder?.addressTo?.building && <p className="text-sm text-gray-700">
          <strong>Building:</strong> {selectedOrder?.addressTo?.building || "N/A"}
        </p>}
        {selectedOrder?.addressTo?.apartment && <p className="text-sm text-gray-700">
          <strong>Apartment:</strong> {selectedOrder?.addressTo?.apartment || "N/A"}
        </p>}
        {selectedOrder?.addressTo?.instructions && <p className="text-sm text-gray-700">
          <strong>Instructions:</strong> {selectedOrder?.addressTo?.instructions || "N/A"}
        </p>}
        {selectedOrder?.addressTo?.google_map_coordinates && <p className="text-sm text-gray-700">
          <strong>Location:</strong> 
          <a 
            href={`https://www.google.com/maps?q=${selectedOrder?.addressTo?.google_map_coordinates.x},${selectedOrder?.addressTo?.google_map_coordinates.z}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-500 underline"
          >
            View on Google Maps
          </a>
        </p>}
      </div>}


              
      {/* Items List */}
      <div className="mb-4">
        {/* Items Section */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Items</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {selectedOrder.carts.map((item, index) => (
              <div key={index} className="flex items-center rounded-lg p-3">
                <img
                  src={item.product && item.product.image}
                  alt={item.product && item.product.name}
              
                  className="w-16 h-16 rounded-lg object-cover mr-4"
                />
                <div className="flex flex-col flex-grow">
                <div className="text-xs text-gray-600 font-bold"> {item.product.name}</div>
                  <div className="text-xs text-gray-600">Quantity: {item.quantity}</div>
                  <div className="text-xs text-gray-600">
                    {item?.notes?.split('\n').map((line, index) => (
                      <p key={index}>{line}</p>
                    ))}
                  </div>
                  <div className="flex space-x-2 mt-2">
                    <button
                      className="flex border border-darkGrey items-center justify-center w-8 h-8 text-white rounded-lg hover:border-yellow transition"
                      onClick={() => handleEditItem(item)}
                    >
                      <PencilIcon className="w-4 h-4 text-darkGrey hover:text-yellow" />
                    </button>
                    <button
                      className="flex border border-darkGrey items-center justify-center w-8 h-8 text-white rounded-lg hover:border-yellow transition"
                      onClick={() => handleDeleteItem(index)}
                    >
                      <TrashIcon className="w-4 h-4 text-darkGrey hover:text-yellow" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

                         {selectedOrder.items.map((item, index) => (
                          <div key={index} className="flex items-center rounded-lg p-3">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-16 h-16 rounded-lg object-cover mr-4"
                            />
                            <div className="flex flex-col flex-grow">
                            <div className="text-xs text-gray-600 font-bold"> {item.name}</div>
                              <div className="text-xs text-gray-600">
                                {item?.notes?.split('\n').map((line, index) => (
                                  <p key={index}>{line}</p>
                                ))}
                              </div>
                              <div className="flex space-x-2 mt-2">
                               
                              </div>
                            </div>
                          </div>
                        ))}
          </div>
        </div>
      </div>

      {/* Promo and Receiver Number */}
      {selectedOrder.promoCode && (
        <div className="flex">
          <label className="block text-sm font-bold text-gray-700 mb-1">PromoCode:</label>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {selectedOrder.promoCode.code} {selectedOrder.promoCode.amount}%
          </label>
        </div>
      )}

      {selectedOrder.receiverNumber && (
        <div className="flex">
          <label className="block text-sm font-bold text-gray-700 mb-1">Receiver Number:</label>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {selectedOrder.receiverNumber}
          </label>
        </div>
      )}


              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
              readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
                rows="3"
                defaultValue={selectedOrder.notes}
              ></textarea>


              <div className="flex justify-center space-x-2">
                <button
                  className="px-5 py-1 bg-white border font-inter border-gray-700 text-gray-700 text-sm rounded-lg hover:bg-yellow hover:text-white hover:border-white "

                >
                  PRINT
                </button>
                <button className="px-5 py-1 bg-white font-inter  border border-gray-700 text-gray-700 text-sm  rounded-lg hover:bg-yellow hover:text-white hover:border-white"
                  onClick={closeModal}>
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        )}
</div>
    </section>
  );
};

export default Report;
